import { computed, ref } from "@vue/composition-api";
import store from "@/store";
import axios from "@/libs/axios";
import {debounce} from "lodash";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import {EventBus} from "@/libs/event-bus";
import { getCurrentInstance } from "@vue/composition-api";
import { result } from "lodash";

export function usePenetrationForm(emit) {
  const vm = getCurrentInstance().proxy;
  const toast = useToast();
  const { t } = useI18nUtils();

  const mapOptions = (item) => ({
    value: item.id,
    label: `${item.sirh ?? ''}-${item.description}`,
    categories: [item?.category],
    region: item?.region?.name,
    subregion: item?.sector?.name,
    sirh: item?.sirh,
    municipality: item?.municipality?.name,
    rems: item?.rems,
    id_seproban: item?.id_seproban,
    unit_with_penetration_testing_active: item?.unit_with_penetration_testing_active
  });
  const datePickerConfig = {
    maxDate: new Date(),
    dateFormat: "d/m/Y",
    allowInput: false,
    locale: {
      firstDayOfWeek: 1,
    },
  };
  const typeOfScenarioOptions = [
    "Ingeniería Social",
    "Acceso no Autorizado / Intrusión",
    "Paquete Sospechoso",
    "Alarma",
    "Sustracción de Activos",
  ];
  const eventTypeOptions = [
    "Real",
    "Prueba",
  ];
  const resultsOptions = [
    "Aprobado",
    "Fallido",
    "No realizado",
  ];
  const defaultUserData = {
    name: '',
    profile: '',
    soeid: ''
  }
  const user = store.getters["user/getUser"];
  const translations = {
    "operational_security.evaluation.error_send_form": t("operational_security.evaluation.error_send_form"),
    "operational_security.evaluation.success_send_form": t("operational_security.evaluation.success_send_form"),
    "operational_security.evaluation.answers_successfully": t("operational_security.evaluation.answers_successfully"),
    "operational_security.evaluation.answers_error": t("operational_security.evaluation.answers_error"),
    "operational_security.evaluation.review_successfully": t("operational_security.evaluation.review_successfully"),
    "operational_security.evaluation.review_error": t("operational_security.evaluation.review_error"),
    "operational_security.evaluation.status_successfully": t("operational_security.evaluation.status_successfully"),
    "operational_security.evaluation.status_error": t("operational_security.evaluation.status_error"),
    "operational_security.evaluation.reopen_successfully": t("operational_security.evaluation.reopen_successfully"),
    "operational_security.evaluation.reopen_error": t("operational_security.evaluation.reopen_error"),
    "testing.penetration.required": t("testing.penetration.required"),
    "testing.penetration.perticipantRequired": t("testing.penetration.perticipantRequired"),
  };

  const requiredFields = ref([
    'rems',
    'registrationDate',
    'responsibleOfExercise',
    'typeOfScenario',
    'descriptionHypothesis',
    'eventType',
    'detailedDescription',
    'securityParticipants',
    'additionalParticipants',
    'employeesToEvaluated',
    'result',
  ])
  const form = ref({
    rems: {
      rems: null,
      sirh: null,
      facility: null
    },
    registrationDate: '',
    responsibleOfExercise: '',
    typeOfScenario: '',
    descriptionHypothesis: '',
    eventType: '',
    detailedDescription: '',
    securityParticipants: [{...defaultUserData}],
    additionalParticipants: [{...defaultUserData}],
    employeesToEvaluated: [{...defaultUserData}],
    result: '',
    correctiveActions: '',
    suggestedActions: '',
    attachedDocuments: [],
    approver: buildUserOptionLabel(),
    penetrationTestingStatus: [],
    lastStatus: null
  })

  const facilityOptions = ref([]);
  const isFormDisabled = ref(false);
  const facilitySearch = ref(null);
  const files = ref([]);
  const existingFiles = ref([]); // when edit
  const deletedFiles = ref([]); // when edit
  const isEdit = ref(false);
  const formId = ref(false);
  const errors = ref({});
  const userSelected = ref(null);
  const userSelectedReasigned = ref(null);
  const userOptions = ref([]);
  const userReopenedOptions = ref([]);
  const validationComment = ref(null);
  const errorValidationComment = ref(null);
  const currentFormStatus = ref(null);
  const isFormFromSameUser = ref(true);
  const referenceShowed = ref("PP-AA-000000");
  const showAlert = ref(false);
  const successProcess = ref(false);
  const disabledButton = ref(false);

  const isDark = computed(() => store.state.appConfig.layout.skin === "dark");
  const formattedFacilityOptions = computed(() => {
    return (facilityOptions.value || [])
      .slice()
      .map(mapOptions);
  });
  const formStatus = computed(() => {
    return {
      withoutValue: false ,
      inProgress: currentFormStatus.value === 'in_progress',
      isRejected: currentFormStatus.value === 'rejected',
      isApproved: currentFormStatus.value === 'approved',
      isReview: currentFormStatus.value === 'in_review',
      isReopen: currentFormStatus.value === 'reopened',
      isCanceled: currentFormStatus.value === 'canceled'
    };
  });

  const canUserSendToReview = computed(() =>  {
    const conditionForForm    = (formStatus.value.inProgress || formStatus.value.isReopen);
    const userCanSendToReview = vm.$can('send_to_review', 'Testing');
    const userIsOwnerForm     = (form.value.lastStatus && (form.value.lastStatus.id_user === user.id));

    return (conditionForForm && userCanSendToReview && userIsOwnerForm && successProcess.value);
  });

  const unitFieldDisabled = computed(() => currentFormStatus.value && currentFormStatus.value !== 'in_progress');

  const onSearchFacility = debounce(async (query, loading) => {
    if (query && query.length) {
      loading(true);
      facilitySearch.value = query;
      await fetchFacilityData();
      loading(false);
    } else {
      facilitySearch.value = '';
      await fetchFacilityData();
    }
  }, 500);

  function isMasterAdmOrSuperUser(profile) {
    const p = profile.toLowerCase()
    return p.includes('masteradmin') || p.includes('superusuario') || p.includes('supervisor')
  }

  function buildUserOptionLabel() {
    const parts = [user.user, user.full_name].filter(Boolean);
    return parts.join(" | ");
  }

  async function fetchFacilityData() {
    try {
      const {data} = await axios.get('/v1/unit', {
        params: {
          q: facilitySearch.value,
          max: 20,
          excludedAtms: true,
          sortBy: 'id',
          'with[]': 'municipality'
        },
      });
      facilityOptions.value = data.data;
    } catch (error) {
      console.error(`FacilitySelect: request error`, error?.response || error);
    }
  }

  const goBackToList = () => {
    // clearForm()
    emit("goBackToList");
  };

  const addNewUserItem = (key) => {
    form.value[key]?.push({...defaultUserData})
  };

  const removeUserItem = (key, index) => {
    if(form.value[key]?.length === 1) return;
    form.value[key]?.splice(index, 1);
  };

  const onChangeFacilitySelect = (event) => {
    showAlert.value = event.unit_with_penetration_testing_active;
    form.value.rems.rems = form.value?.rems.facility?.rems
    form.value.rems.sirh = form.value?.rems.facility?.sirh
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    files.value.push(...newFiles);
    console.log("files", files.value[0].name)
  };

  const getFileType = (filename) => {
    if (!filename || typeof filename !== 'string') return 'file';

    const extension = filename.split('.').pop().toLowerCase();

    const types = {
      image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'],
      pdf: ['pdf'],
      word: ['doc', 'docx', 'rtf', 'odt'],
      excel: ['xls', 'xlsx', 'csv', 'ods'],
      ppt: ['ppt', 'pptx', 'odp'],
      video: ['mp4', 'mkv', 'avi', 'mov', 'webm', 'wmv'],
      audio: ['mp3', 'wav', 'ogg', 'm4a'],
      zip: ['zip', 'rar', '7z', 'tar', 'gz'],
      code: ['js', 'ts', 'html', 'css', 'json', 'php', 'sql', 'go', 'py']
    };

    for (const type of Object.keys(types)) {
      if (types[type].includes(extension)) {
        return type;
      }
    }

    return 'file';
  }

  const removeFile = (index) => {
    files.value.splice(index, 1);
  };

  const getFormData = () => {
    const formData = new FormData();

    for (const file of files.value) {
      formData.append("file[]", file)
      form.value.attachedDocuments.push({name: file.name, url: ""})
    }

    formData.append("data", JSON.stringify(form.value));
    formData.append("template", "Prueba de Cajas de Seguridad");

    if(existingFiles.value.length) {
      formData.append(
        "existing_files",
        JSON.stringify(existingFiles.value)
      )
    }

    if(deletedFiles.value.length) {
      formData.append(
        "deleted_files",
        JSON.stringify(deletedFiles.value)
      )
    }

    return formData;
  }

  const submitForm = async () => {
    const formData = getFormData();

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-HTTP-Method-Override": isEdit.value ? "PUT" : "POST",
      },
    };

    disabledButton.value = true;

    try {
      isFormDisabled.value = true;
      const url = isEdit.value
         ? `v1/testing/penetration/${formId.value}`
         : `v1/testing/penetration`;

      /* const method = isEdit.value ? 'put' : 'post'; */

      const data = await axios.post(url, formData, config);

      contentToast(translations["operational_security.evaluation.answers_successfully"], "success", "CheckIcon")
      getLastStatus(data.data.data.last_status);
      setEditData(data.data.data);

      formId.value = data.data.data.id;
      successProcess.value = true;


      if (currentFormStatus.value === 'reopened') {
        EventBus.emit('testing-validated-or-rejected', { id: formId.value, type: 'penetration_testing_reopened'});
      }

      if (files.value.length) {
        files.value = []
      }
    } catch (error) {
      const errorMessage = error?.error?.response?.data?.message || translations["operational_security.evaluation.answers_error"]
      successProcess.value = false;
      contentToast(errorMessage, "danger", "AlertTriangleIcon")
    } finally {
      isFormDisabled.value = false;
      disabledButton.value = false;
    }
  };

  const getLastStatus = (penetrationTestingStatus) => {
    currentFormStatus.value = penetrationTestingStatus.status.name;
  }

  function contentToast (message, type, icon="CheckIcon") {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon,
        variant: type,
      },
    });
  }

  const validateForm = () => {
    errors.value = {}

    toggleCorrectiveActions()

    requiredFields.value.forEach(key => {
      const value = form.value[key]

      if (
        value === '' ||
        value === null ||
        value === undefined
      ) {
        errors.value[key] = translations["testing.penetration.required"]
      }
    })

    if (!form.value.rems?.facility?.value) {
      errors.value['rems'] = translations["testing.penetration.required"]
    }

    validateParticipants(form.value.securityParticipants, 'securityParticipants')
    validateParticipants(form.value.additionalParticipants, 'additionalParticipants')
    validateParticipants(form.value.employeesToEvaluated, 'employeesToEvaluated')

    return Object.keys(errors.value).length === 0
  }

  const toggleCorrectiveActions = () => {
    const index = requiredFields.value.indexOf('correctiveActions')

    if (form.value.result === 'Fallido') {
      if (index === -1) {
        requiredFields.value.push('correctiveActions')
      }
    } else {
      if (index !== -1) {
        requiredFields.value.splice(index, 1)
      }
    }
  }

  const validateParticipants = (list, fieldName) => {
    const hasValidUser = list.some(user =>
      user.name?.trim() &&
      user.profile?.trim()
    )

    if (!hasValidUser) {
      errors.value[fieldName] = translations["testing.penetration.perticipantRequired"]
    }
  }

  const setEditData = (data) => {
    isEdit.value = true;
    formId.value = data.id;
    referenceShowed.value = data.reference_number;

    getLastStatus(data.last_status);

    const unitLabel = (data.unit) ? `${data.unit.sirh ?? ''}-${data.unit.description}` : null;

    form.value = {
      rems: {
        rems: (data.unit) ? data.unit.rems : null,
        sirh: (data.unit) ? data.unit.sirh: null,
        facility: {
          value: (data.unit) ? data.unit.id : null,
          label: unitLabel
        }
      },
      registrationDate: data.registration_date,
      responsibleOfExercise: data.stage_manager,
      typeOfScenario: data.scenario_type,
      descriptionHypothesis: data.hypothesis_description,
      eventType: data.event_type,
      detailedDescription: data.details_description,
      securityParticipants: data.users.filter(
        user => (user.type === 'security_participant')
      ).map(user => ({
        name: user.name,
        profile: user.position,
        soeid: user.soeid
      })),
      additionalParticipants: data.users.filter(
        user => (user.type === 'additional_participant')
      ).map(user => ({
        name: user.name,
        profile: user.position,
        soeid: user.soeid
      })),
      employeesToEvaluated: data.users.filter(
        user => (user.type === 'employees_evaluated')
      ).map(user => ({
        name: user.name,
        profile: user.position,
        soeid: user.soeid
      })),
      result: data.result,
      correctiveActions: data.corrective_actions,
      suggestedActions: data.suggested_actions,
      attachedDocuments: [],
      approver: buildUserOptionLabel(),
      penetrationTestingStatus: data.penetration_testing_status,
      lastStatus: data.last_status
    }

    existingFiles.value = data.files || [];

    const disabledByStatuses = ['approved','in_review','rejected','canceled'];
    const owenerUserTesting  = (user.id === form.value.lastStatus.id_user);

    if (disabledByStatuses.includes(currentFormStatus.value) || !owenerUserTesting) {
      isFormDisabled.value = true;
    }

    if (currentFormStatus.value === 'in_progress' || currentFormStatus.value === 'in_review') {
      successProcess.value = true;
    }
    //currentFormStatus.value = data.status
    // form.value = {"rems":{"rems":456,"sirh":654,"facility":{"value":14022,"label":"654-Tests28OCT","categories":[{"id":5,"name":"ATM","description":"ATM"}],"region":"Ciudad De México","subregion":"Sector (Test1)","sirh":654,"municipality":"Alcaldía. Benito Juárez","rems":456,"id_seproban":null}},"registrationDate":"01/02/2026","responsibleOfExercise":"Rafa","typeOfScenario":"Ingeniería Social","descriptionHypothesis":"test","eventType":"Prueba","detailedDescription":"dsf","securityParticipants":[{"name":"dfsa","profile":"asd","soeid":""},{"name":"gg","profile":"gg","soeid":""}],"additionalParticipants":[{"name":"asdf","profile":"asdf","soeid":""},{"name":"222","profile":"222","soeid":""}],"employeesToEvaluated":[{"name":"uyyuuy","profile":"uyuuy","soeid":""}],"result":"Fallido","correctiveActions":"fthdft","suggestedActions":"sdfgsdf","attachedDocuments":[{"name":"acuse_cita_placas.pdf","url":""},{"name":"github-logo.png","url":""}],"approver":"Developer"}
    // existingFiles.value = [{"name":"acuse_cita_placas.pdf","url":""},{"name":"github-logo.png","url":""}]
  }

  const removeExistingFile = (index) => {
    deletedFiles.value.push(existingFiles.value[index]);
    existingFiles.value.splice(index, 1);
  };

  const getValidators = async () => {
    await axios.get('v1/testing/user-validators')
      .then(response => {
        userOptions.value = response.data.data
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message ?? translations["operational_security.evaluation.status_error"];
        contentToast(errorMessage, "danger", "AlertTriangleIcon")
      });
  };

  const getUsersReopened = async () => {
    await axios.get('v1/testing/user-reopened')
      .then(response => {
        userReopenedOptions.value = response.data.data
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message ?? translations["operational_security.evaluation.status_error"];
        contentToast(errorMessage, "danger", "AlertTriangleIcon")
      });
  };

  const sendToReview = async () => {
    if (!validateForm()) return;

    const formData = getFormData();

    const config = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }

    formData.append('id_penetration_testing', formId.value);
    formData.append('id_user_validation', userSelected.value);

    disabledButton.value = true;

    await axios.post('v1/testing/penetration/validation', formData, config)
      .then(response => {
        const { data } = response;

        contentToast(data.message, 'success', 'CheckIcon');
        goBackToList();
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  const approve = () => {
    errorValidationComment.value = null;

    if (!validationComment.value) {
      errorValidationComment.value = translations["testing.penetration.required"];
      return;
    }

    const request = {
      id_penetration_testing: formId.value,
      comments: validationComment.value
    }

    disabledButton.value = true;

    axios.post('v1/testing/penetration/approve', request)
      .then(response => {
        const { data } = response;

        EventBus.emit('testing-validated-or-rejected', { id: formId.value, type: 'penetration_testing_validate'});
        contentToast(data.message, 'success', 'CheckIcon');
        goBackToList();
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  const reject = () => {
    errorValidationComment.value = null;

    if (!validationComment.value) {
      errorValidationComment.value = translations["testing.penetration.required"];
      return;
    }

    const request = {
      id_penetration_testing: formId.value,
      comments: validationComment.value
    }

    disabledButton.value = true;

    axios.post('v1/testing/penetration/reject', request)
      .then(response => {
        const { data } = response;

        form.value.penetrationTestingStatus = data.data.penetration_testing_status;

        getLastStatus(data.data.last_status);
        EventBus.emit('testing-validated-or-rejected', { id: formId.value, type: 'penetration_testing_validate'});
        contentToast(data.message, 'success', 'CheckIcon');
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  const reopen = async () => {
    const request = {
      id_penetration_testing: formId.value,
      id_user_reopened: userSelectedReasigned.value
    }

    disabledButton.value = true;

    await axios.post('v1/testing/penetration/reopen', request)
      .then(response => {
        const { data } = response;

        contentToast(data.message, 'success', 'CheckIcon');
        goBackToList();
      })
      .finally(() => {
        disabledButton.value = false;
      });
  }

  const getStatusVariant = (status) => {
    if (!status) return "secondary";

    const statusVariants = {
      in_progress: "primary",
      pending: "warning",
      in_review: "info",
      completed: "success",
      rejected: "danger",
      reopened: "warning",
      approved: "success"
    };

    return statusVariants[status] || "secondary";
  }

  const getStatusLabel = (status) => {
    if (!status) return "-";

    return t(`operational_security.evaluation.status.${status}`);
  }

  const statusesByValidator = ['approved','canceled','rejected','in_review'];

  function formatUser(s) {
    const { name:nameStatus } = s.status;
    const historicalUser      = (statusesByValidator.includes(nameStatus)) ? s.user_validation : s.user;
    const name                = (historicalUser) ? historicalUser.user : '-';
    const email               = (historicalUser) ? historicalUser.email : '-';

    return [name, email].filter(Boolean).join(' | ');
  }

  function getUserName(s) {
    const { name:nameStatus } = s.status;
    const historicalUser      = (statusesByValidator.includes(nameStatus)) ? s.user_validation : s.user;

    return (historicalUser) ? historicalUser.user : '-';
  }

  function getUserEmail(s) {
    const { name:nameStatus } = s.status;
    const historicalUser      = (statusesByValidator.includes(nameStatus)) ? s.user_validation : s.user;

    return (historicalUser) ? historicalUser.email : '-';
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  return {
    form,
    isDark,
    formattedFacilityOptions,
    isFormDisabled,
    datePickerConfig,
    typeOfScenarioOptions,
    resultsOptions,
    eventTypeOptions,
    referenceShowed,
    formStatus,
    canUserSendToReview,
    existingFiles,
    validationComment,
    errors,
    onSearchFacility,
    files,
    goBackToList,
    fetchFacilityData,
    addNewUserItem,
    removeUserItem,
    onChangeFacilitySelect,
    handleFileChange,
    removeFile,
    submitForm,
    setEditData,
    removeExistingFile,
    getValidators,
    userOptions,
    userSelected,
    userSelectedReasigned,
    sendToReview,
    approve,
    reject,
    showAlert,
    userReopenedOptions,
    getUsersReopened,
    reopen,
    errorValidationComment,
    getStatusVariant,
    getStatusLabel,
    formatUser,
    getUserName,
    getUserEmail,
    formatDate,
    disabledButton,
    formId,
    user,
    unitFieldDisabled
  }
}
